import { Dispatch, FormEvent, SetStateAction, useContext, useEffect, useState } from "react";

import { TemplateStepConstants } from "../../../../../../../constants";
import { TemplateStepModalData } from "../../../../../../../models";
import { ServiceError, Status } from "../../../../../../../service/Shared";
import { addOrRemoveWorkflowTemplateStep } from "../../../../../../../service/workflow/WorkflowService.full";
import { useIsLoadingWrapper } from "../../../../../../../utils";
import { Toast } from "../../../../../../../widget";
import { WorkflowTemplateContext } from "../../WorkflowTemplateContext";

interface UseAddTemplateStepModalProps {
  closeModal: () => void;
  templateUuid?: string;
}

interface UseAddTemplateStepModalReturnData {
  step: TemplateStepModalData;
  setStep: Dispatch<SetStateAction<TemplateStepModalData>>;

  handleAddTemplateStep: (e: FormEvent<HTMLFormElement>) => void;
  isHandleAddTemplateStepLoading: boolean;
  onCancel: () => void;
  errors: ServiceError[];
}

export const useAddTemplateStepModal = ({
  closeModal,
  templateUuid,
}: UseAddTemplateStepModalProps): UseAddTemplateStepModalReturnData => {
  const { shouldRefreshWorkflowTemplate, projectPhase } = useContext(WorkflowTemplateContext);
  const [isHandleAddTemplateStepLoading, setIsHandleAddTemplateStepLoading] = useState(false);
  const [errors, setErrors] = useState<ServiceError[]>([]);

  const [step, setStep] = useState<TemplateStepModalData>({
    ...TemplateStepConstants.DEFAULT_ADD_TEMPLATE_STEP,
    defaultPhase: projectPhase,
  });

  const onCancel = (): void => {
    setErrors([]);
    setStep({
      ...TemplateStepConstants.DEFAULT_ADD_TEMPLATE_STEP,
      defaultPhase: projectPhase,
    });
    closeModal();
  };

  const handleAddTemplateStep = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    if (!templateUuid) {
      setErrors([
        {
          code: "NO_TEMPLATE",
          message: "A template must be selected",
        },
      ]);
    } else {
      await addOrRemoveWorkflowTemplateStep({
        defaultStepName: step.defaultStepName,
        defaultPhase: step.defaultPhase as string,
        defaultIsRepeatable: step.defaultIsRepeatable,
        workflowTemplateUuid: templateUuid,
        operation: TemplateStepConstants.ADD_OPERATION,
        workflowTemplateStepUuid: null,
        workflowTemplateStepRowVersion: null,
      }).then((response) => {
        if (response.status === Status.Success) {
          Toast.success({ message: "Template step successfully added" });

          shouldRefreshWorkflowTemplate();
          onCancel();
        }
        if (response.status === Status.Error && response.errors) {
          setErrors(response.errors);
        }
      });
    }
  }, setIsHandleAddTemplateStepLoading);

  useEffect(() => {
    setStep((prevStep) => ({
      ...prevStep,
      defaultPhase: projectPhase,
    }));
  }, [projectPhase]);

  return { step, setStep, isHandleAddTemplateStepLoading, handleAddTemplateStep, onCancel, errors };
};
